import { DietaryInfo, Menu } from '../types/menu'

// Builds schema.org Menu data (JSON-LD) so search engines, maps and AI assistants
// can read the menu as structured data. See https://schema.org/Menu

// Only symbols that clearly mean one currency get a code. "$" and "¥" are shared
// by several currencies, so for those the price is listed without a currency.
const SYMBOL_TO_CODE: Record<string, string> = {
  '€': 'EUR',
  '£': 'GBP',
  '₹': 'INR',
  '₩': 'KRW',
  C$: 'CAD',
  A$: 'AUD',
  R$: 'BRL',
  '₽': 'RUB',
}

const DIET_URLS: Partial<Record<DietaryInfo, string>> = {
  vegetarian: 'https://schema.org/VegetarianDiet',
  vegan: 'https://schema.org/VeganDiet',
  'gluten-free': 'https://schema.org/GlutenFreeDiet',
}

/** ISO currency code when the symbol is unambiguous, otherwise undefined */
export function menuCurrencyCode(menu: Menu): string | undefined {
  return SYMBOL_TO_CODE[menu.currency]
}

export function buildMenuSchema(menu: Menu): Record<string, unknown> {
  const currencyCode = menuCurrencyCode(menu)

  return {
    '@context': 'https://schema.org',
    '@type': 'Menu',
    name: menu.name,
    hasMenuSection: menu.sections.map((section) => ({
      '@type': 'MenuSection',
      name: section.name,
      ...(section.description && { description: section.description }),
      hasMenuItem: section.items.map((item) => {
        const price = item.price === undefined ? '' : String(item.price).replace(/[^\d.]/g, '')
        const diets = (item.dietary || []).map((d) => DIET_URLS[d]).filter(Boolean)
        return {
          '@type': 'MenuItem',
          name: item.name,
          ...(item.description && { description: item.description }),
          ...(price && {
            offers: { '@type': 'Offer', price, ...(currencyCode && { priceCurrency: currencyCode }) },
          }),
          ...(diets.length > 0 && { suitableForDiet: diets.length === 1 ? diets[0] : diets }),
        }
      }),
    })),
  }
}

/** The <script> tag to include in the embed code. Escapes "<" so menu text can't close the tag. */
export function menuSchemaScript(menu: Menu): string {
  const json = JSON.stringify(buildMenuSchema(menu), null, 2).replace(/</g, '\\u003c')
  return `<script type="application/ld+json">\n${json}\n</script>`
}
